import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import { Progress } from "../components/ui/progress";
import { ArrowLeft, Clock, CheckCircle2, BookOpen, ChevronLeft, ChevronRight, Send } from "lucide-react";
import { toast } from "sonner";

const difficultyLabels = {
  1: "Beginner",
  2: "Intermediate",
  3: "Advanced",
  4: "Editorial",
};

const PracticePage = () => {
  const navigate = useNavigate();
  const { passageId } = useParams();
  const { user, token } = useAuth();

  const [passage, setPassage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Timer state
  const [timerEnabled, setTimerEnabled] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);

  useEffect(() => {
    fetchPassage();
  }, [passageId]);

  useEffect(() => {
    let interval;
    if (timerRunning && timerEnabled) {
      interval = setInterval(() => {
        setTimeElapsed((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerRunning, timerEnabled]);

  const fetchPassage = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/passages/${passageId}`);
      setPassage(response.data);
      setTimerRunning(true);
    } catch (error) {
      console.error("Error fetching passage:", error);
      toast.error("Failed to load passage");
      navigate("/practice");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleAnswerSelect = (questionId, optionIndex) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: optionIndex,
    }));
  };

  const handleSubmit = async () => {
    const questions = passage?.questions || [];
    const unanswered = questions.filter((q) => answers[q.question_id] === undefined);

    if (unanswered.length > 0) {
      toast.warning(`You have ${unanswered.length} unanswered question(s)`);
      return;
    }

    setSubmitting(true);
    setTimerRunning(false);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/practice/submit`,
        {
          passage_id: passageId,
          answers,
          time_taken: timeElapsed,
          mode: user?.mode || "general",
        },
        { headers, withCredentials: true }
      );

      toast.success("Practice submitted!");
      navigate(`/results/${response.data.attempt_id}`);
    } catch (error) {
      console.error("Error submitting:", error);
      toast.error("Failed to submit practice");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading passage...</p>
        </div>
      </div>
    );
  }

  if (!passage) {
    return null;
  }

  const questions = passage.questions || [];
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / questions.length) * 100;
  const currentQ = questions[currentQuestion];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 glass-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" onClick={() => navigate("/practice")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>

            <div className="flex items-center gap-6">
              {/* Timer Toggle */}
              <div className="flex items-center gap-2">
                <Switch
                  id="timer"
                  checked={timerEnabled}
                  onCheckedChange={setTimerEnabled}
                  data-testid="timer-toggle"
                />
                <Label htmlFor="timer" className="text-sm text-slate-600">
                  Timer
                </Label>
              </div>

              {/* Timer Display */}
              {timerEnabled && (
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg">
                  <Clock className="w-4 h-4 text-slate-600" />
                  <span className="timer-display text-slate-900">{formatTime(timeElapsed)}</span>
                </div>
              )}

              {/* Progress */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">
                  {answeredCount}/{questions.length}
                </span>
                <Progress value={progress} className="w-24 h-2" />
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Passage Section */}
          <div className="lg:col-span-3">
            <Card className="mb-6">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Badge variant="secondary" className="capitalize">
                    {passage.category}
                  </Badge>
                  <Badge className={`diff-${passage.difficulty}`}>{difficultyLabels[passage.difficulty]}</Badge>
                </div>

                <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">{passage.title}</h1>

                <ScrollArea className="h-[50vh] lg:h-[60vh]">
                  <div className="passage-text whitespace-pre-wrap pr-4">{passage.content}</div>
                </ScrollArea>

                {/* Vocabulary Section */}
                {passage.vocabulary && passage.vocabulary.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Key Vocabulary</h3>
                    <div className="flex flex-wrap gap-2">
                      {passage.vocabulary.map((v, i) => (
                        <div
                          key={i}
                          className="px-3 py-1.5 bg-slate-100 rounded-lg text-sm"
                          title={v.meaning}
                        >
                          <span className="font-medium text-slate-900">{v.word}</span>
                          <span className="text-slate-500 ml-1">- {v.meaning}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Questions Section */}
          <div className="lg:col-span-2">
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-slate-900">
                    Question {currentQuestion + 1} of {questions.length}
                  </h2>
                  <Badge variant="outline" className="capitalize">
                    {currentQ?.question_type}
                  </Badge>
                </div>

                {currentQ && (
                  <div className="space-y-4">
                    <p className="question-text text-lg">{currentQ.question_text}</p>

                    <div className="space-y-3">
                      {currentQ.options.map((option, idx) => (
                        <div
                          key={idx}
                          onClick={() => handleAnswerSelect(currentQ.question_id, idx)}
                          className={`option-card ${
                            answers[currentQ.question_id] === idx ? "selected" : ""
                          }`}
                          data-testid={`option-${idx}`}
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                answers[currentQ.question_id] === idx
                                  ? "border-slate-900 bg-slate-900"
                                  : "border-slate-300"
                              }`}
                            >
                              {answers[currentQ.question_id] === idx && (
                                <CheckCircle2 className="w-4 h-4 text-white" />
                              )}
                            </div>
                            <span className="text-slate-700">{option}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentQuestion((prev) => Math.max(0, prev - 1))}
                    disabled={currentQuestion === 0}
                    data-testid="prev-question-btn"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>

                  {currentQuestion < questions.length - 1 ? (
                    <Button
                      onClick={() => setCurrentQuestion((prev) => Math.min(questions.length - 1, prev + 1))}
                      data-testid="next-question-btn"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="submit-btn"
                    >
                      {submitting ? (
                        "Submitting..."
                      ) : (
                        <>
                          Submit
                          <Send className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {/* Question Navigator */}
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <p className="text-sm text-slate-600 mb-3">Jump to question:</p>
                  <div className="flex flex-wrap gap-2">
                    {questions.map((q, idx) => (
                      <button
                        key={q.question_id}
                        onClick={() => setCurrentQuestion(idx)}
                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                          currentQuestion === idx
                            ? "bg-slate-900 text-white"
                            : answers[q.question_id] !== undefined
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                        data-testid={`question-nav-${idx}`}
                      >
                        {idx + 1}
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PracticePage;
